import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  Settings,
  Shield,
  UserRound,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { trackEvent } from "@/lib/firebase";

const DEFAULT_NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Heritage", to: "/heritage" },
  { label: "Our Community", to: "/global-network" },
  { label: "News", to: "/news" },
  { label: "Articles", to: "/articles" },
  { label: "Blogs", to: "/blogs" },
  { label: "Initiatives", to: "/initiatives" },
  { label: "Organizations", to: "/organizations" },
  { label: "Opportunities", to: "/opportunities" },
  { label: "Events", to: "/events" },
  { label: "Media", to: "/media" },
  { label: "Contact", to: "/contact" },
];
const PRIMARY = ["/", "/about", "/heritage", "/global-network"],
  EXPLORE_GROUPS = [
    ["Content", ["/news", "/articles", "/blogs"]],
    [
      "Community",
      ["/initiatives", "/organizations", "/opportunities", "/events"],
    ],
    ["Discover", ["/media", "/contact"]],
  ];
const testKey = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Header() {
  const [drawer, setDrawer] = useState(false),
    [scrolled, setScrolled] = useState(false),
    [searchOpen, setSearchOpen] = useState(false),
    [exploreOpen, setExploreOpen] = useState(false),
    [accountOpen, setAccountOpen] = useState(false),
    [q, setQ] = useState(""),
    [results, setResults] = useState([]),
    [siteConfig, setSiteConfig] = useState(null),
    [viewport, setViewport] = useState(() => window.innerWidth);
  const location = useLocation(),
    navigate = useNavigate(),
    { user, isStaff, logout } = useAuth(),
    exploreRef = useRef(null),
    accountRef = useRef(null);
  useEffect(() => {
    const resize = () => setViewport(window.innerWidth);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  useEffect(() => {
    const scroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", scroll);
    return () => window.removeEventListener("scroll", scroll);
  }, []);
  useEffect(() => {
    api
      .get("/site-config")
      .then((r) => setSiteConfig(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    setDrawer(false);
    setSearchOpen(false);
    setExploreOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = drawer ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawer]);
  useEffect(() => {
    const close = (e) => {
        if (!exploreRef.current?.contains(e.target)) setExploreOpen(false);
        if (!accountRef.current?.contains(e.target)) setAccountOpen(false);
      },
      key = (e) => {
        if (e.key === "Escape") {
          setDrawer(false);
          setSearchOpen(false);
          setExploreOpen(false);
          setAccountOpen(false);
        }
      };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", key);
    };
  }, []);
  useEffect(() => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(
      () =>
        api
          .get(`/search?q=${encodeURIComponent(q)}`)
          .then((r) => setResults(r.data.results || []))
          .catch(() => setResults([])),
      250,
    );
    return () => clearTimeout(t);
  }, [q]);
  const builderHeader = siteConfig?.builder?.header || {};
  const device = viewport < 768 ? "mobile" : viewport < 1280 ? "tablet" : "desktop";
  const deviceHeader = device === "desktop" ? {} : builderHeader.responsive?.[device] || {};
  const header = { ...builderHeader, ...deviceHeader };
  const items = useMemo(() => {
    const builderNavigation = siteConfig?.builder?.navigation?.items || [];
    const flatten = (rows) =>
      rows.flatMap((n) => [n, ...flatten(n.children || [])]);
    const source = builderNavigation.length
      ? flatten(builderNavigation)
      : siteConfig?.navigation?.items || [];
    const cms =
      source
        ?.filter((n) => n.visible !== false)
        .map((n) => ({
          label: n.title,
          to: n.url,
          newTab: n.new_tab || n.newTab,
        })) || [];
    const byUrl = new Map(cms.map((n) => [n.to, n]));
    return DEFAULT_NAV.map((n) => byUrl.get(n.to) || n).filter(
      (n) => !cms.length || byUrl.has(n.to),
    );
  }, [siteConfig]);
  const byUrl = (to) => items.find((n) => n.to === to),
    primary = PRIMARY.map(byUrl).filter(Boolean),
    groups = EXPLORE_GROUPS.map(([title, urls]) => [
      title,
      urls.map(byUrl).filter(Boolean),
    ]).filter(([, x]) => x.length),
    exploreActive = groups.some(([, links]) =>
      links.some(
        (n) =>
          location.pathname === n.to ||
          location.pathname.startsWith(`${n.to}/`),
      ),
    );
  const logo =
      header.logo?.public_url ||
      siteConfig?.branding?.logo_url ||
      "/assets/dirnation-logo.png",
    solid = scrolled || location.pathname !== "/" || drawer;
  const accountLinks = user
    ? [
        { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
        ...(isStaff
          ? [{ label: "Admin CMS", to: "/admin", icon: Shield }]
          : []),
        { label: "My Profile", to: "/dashboard#profile", icon: UserRound },
        { label: "Settings", to: "/dashboard#settings", icon: Settings },
      ]
    : [
        { label: "Login", to: "/login", icon: UserRound },
        { label: "Create Account", to: "/signup", icon: UserRound },
      ];
  return (
    <header
      data-testid="site-header"
      style={{
        backgroundColor: solid
          ? header.background || undefined
          : undefined,
        borderBottomWidth: header.border === false ? 0 : undefined,
      }}
      className={`${header.sticky === false ? "relative" : "fixed inset-x-0 top-0"} z-50 transition ${solid ? "border-b border-dir-gold/20 bg-dir-deep/95 shadow-lg shadow-black/10 backdrop-blur-xl" : "bg-transparent"}`}
    >
      <div className="mx-auto max-w-[1400px]" style={{ paddingInline: header.spacing || undefined }}>
        <div
          className="flex items-center gap-2 sm:gap-3"
          style={{ height: header.height || 72 }}
        >
          <Link to="/" data-testid="logo-link" className="mr-auto shrink-0">
            <img
              src={logo}
              alt={`${siteConfig?.branding?.site_name || "DIR Nation"} — ${siteConfig?.branding?.tagline || "One People, Shared Future"}`}
              style={{ maxWidth: header.logoSize || undefined }}
              className="h-12 w-auto max-w-[130px] object-contain sm:h-14 sm:max-w-[160px]"
            />
          </Link>
          {header.navigation !== false && (
            <nav
              className="hidden items-center gap-1 xl:flex"
              aria-label="Primary navigation"
            >
              {primary.map((n) => (
                <NavLink
                  key={n.to}
                  item={n}
                  active={location.pathname === n.to}
                />
              ))}
              <div ref={exploreRef} className="relative">
                <button
                  data-testid="explore-toggle"
                  onClick={() => setExploreOpen((x) => !x)}
                  aria-expanded={exploreOpen}
                  className={`flex items-center gap-1 rounded-full px-3 py-2 text-[13px] font-medium ${exploreActive || exploreOpen ? "text-dir-gold" : "text-dir-muted hover:text-dir-text"}`}
                >
                  Explore{" "}
                  <ChevronDown
                    className={`h-4 w-4 transition ${exploreOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {exploreOpen && <ExploreMenu groups={groups} />}
              </div>
            </nav>
          )}
          {header.search !== false && (
            <button
              data-testid="search-toggle"
              aria-label="Open global search"
              onClick={() => setSearchOpen((x) => !x)}
              className="rounded-full p-2 text-dir-muted transition hover:bg-dir-gold/10 hover:text-dir-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold"
            >
              <Search className="h-5 w-5" />
            </button>
          )}
          {header.account !== false && (
            <div ref={accountRef} className="relative">
              <button
                data-testid="user-menu-toggle"
                onClick={() => setAccountOpen((x) => !x)}
                aria-expanded={accountOpen}
                className="flex max-w-[112px] items-center gap-1.5 rounded-full border border-dir-gold/25 px-2.5 py-2 text-xs text-dir-text hover:bg-dir-gold/10 sm:max-w-[150px] sm:px-3 sm:text-sm"
              >
                <span className="truncate">
                  {user ? user.first_name || "Account" : "DIR Nation"}
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
              </button>
              {accountOpen && (
                <AccountMenu links={accountLinks} user={user} logout={logout} />
              )}
            </div>
          )}
          {!user && header.cta !== false && header.cta?.visible !== false && (
            <Link
              to={header.cta?.url || "/signup"}
              onClick={() =>
                trackEvent("join_dir_nation_click", { placement: "header" })
              }
              data-testid="join-cta"
              className="hidden shrink-0 rounded-full bg-gradient-to-r from-dir-warm to-dir-gold px-5 py-2.5 text-[13px] font-bold text-dir-deep shadow-lg shadow-dir-gold/20 hover:brightness-110 xl:inline-flex"
            >
              {header.cta?.label || "JOIN DIR NATION"}
            </Link>
          )}
          {header.hamburger !== false && <button
            data-testid="mobile-menu-toggle"
            aria-label={drawer ? "Close menu" : "Open menu"}
            onClick={() => setDrawer((x) => !x)}
            className="rounded-full p-2 text-dir-text hover:bg-dir-gold/10 xl:hidden"
          >
            {drawer ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>}
        </div>
        {searchOpen && (
          <div className="pb-4" data-testid="search-panel">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dir-gold" />
              <input
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search DIR Nation..."
                className="w-full rounded-xl border border-dir-gold/20 bg-dir-elevated py-3 pl-11 pr-4 text-sm text-dir-text placeholder:text-dir-faint focus:border-dir-gold focus:ring-2 focus:ring-dir-gold/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold"
              />
            </div>
            {results.length > 0 && (
              <div className="mt-2 max-h-80 overflow-auto rounded-xl border border-dir-gold/15 bg-dir-deep p-2 shadow-2xl">
                {results.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      navigate(r.url);
                      setQ("");
                    }}
                    className="flex w-full flex-col items-start rounded-lg px-3 py-2 text-left hover:bg-dir-gold/10"
                  >
                    <span className="text-[10px] uppercase tracking-wider text-dir-gold">
                      {r.type}
                    </span>
                    <span className="text-sm text-dir-text">{r.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {drawer && (
        <MobileDrawer
          primary={primary}
          groups={groups}
          accountLinks={accountLinks}
          user={user}
          isStaff={isStaff}
          logout={logout}
          headerHeight={header.height || 72}
        />
      )}
    </header>
  );
}
function NavLink({ item, active }) {
  return (
    <Link
      to={item.to}
      target={item.newTab ? "_blank" : undefined}
      data-testid={`nav-${testKey(item.label)}`}
      className={`rounded-full px-3 py-2 text-[13px] font-medium transition ${active ? "text-dir-gold" : "text-dir-muted hover:text-dir-text"}`}
    >
      {item.label}
    </Link>
  );
}
function ExploreMenu({ groups }) {
  return (
    <div
      data-testid="explore-menu"
      className="absolute right-0 top-full mt-2 grid w-[460px] grid-cols-3 gap-2 rounded-2xl border border-dir-gold/20 bg-dir-deep/98 p-4 shadow-2xl backdrop-blur-xl"
    >
      {groups.map(([title, links]) => (
        <div key={title}>
          <div className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[.18em] text-dir-gold">
            {title}
          </div>
          {links.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="block rounded-lg px-2 py-2 text-sm text-dir-muted hover:bg-dir-gold/10 hover:text-dir-text"
            >
              {n.label}
            </Link>
          ))}
        </div>
      ))}
    </div>
  );
}
function AccountMenu({ links, user, logout }) {
  return (
    <div
      data-testid="account-menu"
      className="absolute right-0 top-full mt-2 w-52 rounded-2xl border border-dir-gold/20 bg-dir-deep/98 p-2 shadow-2xl backdrop-blur-xl"
    >
      {links.map(({ label, to, icon: Icon }) => (
        <Link
          key={label}
          to={to}
          className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-dir-muted hover:bg-dir-gold/10 hover:text-dir-text"
        >
          <Icon className="h-4 w-4 text-dir-gold" />
          {label}
        </Link>
      ))}
      {user && (
        <button
          onClick={logout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-dir-muted hover:bg-dir-gold/10 hover:text-dir-text"
        >
          <LogOut className="h-4 w-4 text-dir-gold" />
          Logout
        </button>
      )}
    </div>
  );
}
function MobileDrawer({
  primary,
  groups,
  accountLinks,
  user,
  isStaff,
  logout,
  headerHeight,
}) {
  return (
    <div
      className="fixed inset-x-0 z-50 bg-black/50 xl:hidden"
      style={{ top: headerHeight, height: `calc(100dvh - ${headerHeight}px)` }}
      data-testid="mobile-menu"
    >
      <aside className="ml-auto h-full w-full max-w-sm overflow-y-auto border-l border-dir-gold/20 bg-dir-deep p-5 shadow-2xl">
        <nav className="space-y-1">
          {primary.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="block rounded-xl px-3 py-3 text-sm font-medium text-dir-text hover:bg-dir-gold/10"
            >
              {n.label}
            </Link>
          ))}
        </nav>
        {groups.map(([title, links]) => (
          <section key={title} className="mt-6">
            <h2 className="px-3 text-[10px] font-semibold uppercase tracking-[.2em] text-dir-gold">
              {title}
            </h2>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {links.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="rounded-xl px-3 py-2.5 text-sm text-dir-muted hover:bg-dir-gold/10 hover:text-dir-text"
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </section>
        ))}
        <section className="mt-6 border-t border-dir-gold/15 pt-5">
          <h2 className="px-3 text-[10px] font-semibold uppercase tracking-[.2em] text-dir-gold">
            Account
          </h2>
          {accountLinks.map(({ label, to }) => (
            <Link
              key={label}
              to={to}
              className="block rounded-xl px-3 py-2.5 text-sm text-dir-muted hover:bg-dir-gold/10"
            >
              {label}
            </Link>
          ))}
          {user && (
            <button
              onClick={logout}
              className="w-full rounded-xl px-3 py-2.5 text-left text-sm text-dir-muted hover:bg-dir-gold/10"
            >
              Logout
            </button>
          )}
        </section>
        {!user && (
          <Link
            to="/signup"
            className="mt-6 block rounded-full bg-gradient-to-r from-dir-warm to-dir-gold px-5 py-3 text-center text-sm font-bold text-dir-deep"
          >
            JOIN DIR NATION
          </Link>
        )}
      </aside>
    </div>
  );
}
